import * as React from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { labels } from "../../constants/label.constants"
import useSiteMetadata from "../../hooks/useSiteMetadata"
import useScreenSize from "../../hooks/useScreenSize"
import { convertToWebp, getImageData } from "../../helpers/ImageHelper"
import { get } from "../../helpers/utils"

const SeoHead = ({
  title,
  metaDescription,
  metaLongDescription,
  ogTitle,
  ogDescription,
  ogLongDescription,
  ogType,
  ogUrl,
  ogImage,
  featuredImage,
  metaLongKeywords,
  metaKeywords,
  pageType,
  preloadComp,
  languageCode,
  twitterTitle,
  noIndex,
  noFollow,
  canonicalLink,
  slug,
}) => {
  const siteMetadata = useSiteMetadata()
  const screen = useScreenSize()
  const seo = {
    title: title || siteMetadata?.title,
    description:
      metaDescription || metaLongDescription || siteMetadata?.description,
    ogType: ogType || "",
    ogUrl: `${siteMetadata?.siteUrl}${ogUrl || slug || ""}`,
    ogTitle: ogTitle || title || siteMetadata.title,
    ogDescription:
      ogDescription ||
      ogLongDescription ||
      metaDescription ||
      siteMetadata.description,
    pageType: pageType || "",
    canonicalLink: `${siteMetadata?.siteUrl}${
      canonicalLink || ogUrl || slug || ""
    }`,
  }

  const preparedTitle = seo.title || labels?.pageTitle

  const robotsIndex = noIndex ? "noindex" : "index"
  const robotsFollow = noFollow ? "nofollow" : "follow"

  const robotsContent =
    process.env.GATSBY_ACTIVE_ENV !== "production"
      ? "noindex, nofollow"
      : `${robotsIndex}, ${robotsFollow}`

  // preload first contentful image
  function renderImage(compObj) {
    const obj = compObj ? Object.values(compObj)[0] : ""
    if (screen?.isDesktop && obj && obj?.images?.imageDesktop?.url) {
      const cImgObject = getImageData(obj?.images?.imageDesktop, null)
      const imgW = get(cImgObject, "width")
      const imgH = get(cImgObject, "height")
      return (
        <link
          rel="preload"
          as="image"
          href={convertToWebp(cImgObject?.url)}
          // fetchpriority="high"
          width={imgW}
          height={imgH}
        ></link>
      )
    } else if (screen?.isMobile && obj && obj?.images?.imageMobile?.url) {
      const cImgObject = getImageData(obj?.images?.imageMobile, null)
      const imgW = get(cImgObject, "width")
      const imgH = get(cImgObject, "height")
      return (
        <link
          rel="preload"
          as="image"
          href={convertToWebp(cImgObject?.url)}
          // fetchpriority="high"
          width={imgW}
          height={imgH}
        ></link>
      )
    }
  }

  const lang = languageCode == null || languageCode == "" ? "en" : languageCode

  return (
    <Helmet htmlAttributes={{ lang: lang, prefix: "og: https://ogp.me/ns#" }}>
      <title>{preparedTitle}</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta property="http-equiv" content="text/html; charset=utf-8" />
      <meta name="description" content={seo.description} />
      {(metaKeywords || metaLongKeywords) && (
        <meta name="keywords" content={metaKeywords || metaLongKeywords} />
      )}
      <meta property="og:title" content={seo.ogTitle} />
      <meta property="og:description" content={seo.ogDescription} />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content={
          ogImage?.url ||
          featuredImage?.url ||
          "https://www.mondelezinternational.com/assets/logo/mdlz-logo.jpg"
        }
      />
      <meta name="og:url" content={seo.ogUrl} />
      <meta name="og:site_name" content={labels?.pageTitle} />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={siteMetadata?.author || ``} />
      <script type="application/ld+json">
        {`{"tickerSymbol":"MDLZ","address":[{"addressLocality":"Chicago","addressRegion":"IL","postalCode":"60607","streetAddress":"905 West Fulton Market, Suite 200","@context":"http://schema.org","@type":"PostalAddress"}],"logo":"https://www.mondelezinternational.com/assets/logo/mdlz-logo.jpg","description":"Mondelēz International, Inc. (NASDAQ: MDLZ) is one of the world’s largest snacks companies.","name":"Mondelēz International, Inc","url":"https://www.mondelezinternational.com","@context":"http://schema.org","@type":"Corporation"}`}
      </script>
      <meta name="twitter:title" content={twitterTitle || ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta
        property="twitter:image"
        content={
          ogImage?.url ||
          featuredImage?.url ||
          "https://www.mondelezinternational.com/assets/logo/mdlz-logo.jpg"
        }
      />
      <meta property="pageType" content={seo.pageType} />
      {preloadComp && renderImage(preloadComp)}
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={seo.canonicalLink} />
    </Helmet>
  )
}

SeoHead.propTypes = {
  title: PropTypes.string,
  metaDescription: PropTypes.string,
  metaLongDescription: PropTypes.string,
  ogLongDescription: PropTypes.string,
  metaLongKeywords: PropTypes.string,
  ogTitle: PropTypes.string,
  ogDescription: PropTypes.string,
  ogType: PropTypes.string,
  ogUrl: PropTypes.string,
  ogImage: PropTypes.object,
  featuredImage: PropTypes.object,
  url: PropTypes.string,
  metaKeywords: PropTypes.string,
  pageType: PropTypes.string,
  preloadComp: PropTypes.object,
  twitterTitle: PropTypes.string,
  noIndex: PropTypes.bool,
  noFollow: PropTypes.bool,
  languageCode: PropTypes.bool,
  slug: PropTypes.string,
  canonicalLink: PropTypes.string,
}

export default SeoHead